/**
 * Parseia uma data do Notion preservando o dia exato sem conversão de timezone.
 *
 * O Notion retorna datas como strings "YYYY-MM-DD" (sem horário).
 * Quando usamos `new Date("2025-12-11")`, o JavaScript interpreta como UTC meia-noite,
 * causando conversão de timezone que pode fazer o dia voltar (ex: em -03:00).
 *
 * Esta função parseia manualmente a string e cria uma data local,
 * preservando o dia exato que vem do Notion.
 *
 * @param date - Pode ser:
 *   - String no formato "YYYY-MM-DD"
 *   - Objeto `{ start: "YYYY-MM-DD", end?: "YYYY-MM-DD" }`
 *   - `Date` (retorna como está)
 *   - `null` ou `undefined` (retorna `null`)
 * @returns `Date | null` - Data parseada como local ou null se inválida
 */
export function parseNotionDate(date: any): Date | null {
  if (!date) return null;

  // Se já é uma Date, retorna como está
  if (date instanceof Date) {
    return date;
  }

  // Extrair string da data
  let dateString: string | null = null;

  if (typeof date === 'string') {
    dateString = date;
  } else if (typeof date === 'object') {
    dateString = date.start || date.end || null;
  }

  if (!dateString) {
    return null;
  }

  // Parsear manualmente "YYYY-MM-DD" para evitar conversão de timezone
  // Exemplo: "2025-12-11" -> new Date(2025, 11, 11) (mês é 0-indexed)
  const dateRegex = /^(\d{4})-(\d{2})-(\d{2})/;
  const match = dateRegex.exec(dateString);
  if (!match) {
    // Se não for formato YYYY-MM-DD, tenta usar Date normal como fallback
    const parsed = new Date(dateString);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const [, yearStr, monthStr, dayStr] = match;
  const year = Number.parseInt(yearStr, 10);
  const month = Number.parseInt(monthStr, 10) - 1; // Mês é 0-indexed (0-11)
  const day = Number.parseInt(dayStr, 10);

  // Validar valores
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
    return null;
  }

  // Criar data local (sem conversão de timezone)
  const localDate = new Date(year, month, day);

  // Verificar se a data é válida
  if (Number.isNaN(localDate.getTime())) {
    return null;
  }

  return localDate;
}
